import React from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Page } from 'framework7-react';
import {
  APP_EXTERNAL_EVENTS,
  DOC_TYPE_DOCUMENT,
  DOC_TYPE_FORM,
  DOC_TYPE_ID_CHECK,
  DOC_TYPE_IMMEDIATE_CONFIRMATION,
  DOC_TYPE_INFORMATION,
  DOC_TYPE_PAYMENT,
  DOC_TYPE_PDF_FORM,
  DOCUMENT_DISPLAY_MODE_DESKTOP_WINDOW,
  DOCUMENT_DISPLAY_MODE_INPAGE,
  DOCUMENT_EXTERNAL_EVENTS
} from '../helpers/utils';
import DesktopDocEntry from './DesktopDocEntry';
import { gaClickOnDocType, gaeList, gaListPageView } from './ga';
import { f7navigator } from '../helpers/navigator';
import withDesktopUpload from '../helpers/withDesktopUpload';
import DesktopFullPage from '../desktop/DesktopFullPage';
import II18 from '../translate/II18';
import GalleryInPage from '../gallery/desktop/GalleryInPage';
import Viewer from '../viewer/ViewerContainer';
import {
  ExpiredWarningBlock,
  StepperInPageContent,
  RequestVerticalSteps,
  sendTitleDefault,
  StartContent,
  SuccessMessageBlock
} from './Elements';
import Payment from '../payment/desktop/InPagePayment';
import IdCheckInPage from '../idCheck/desktop/IdCheckInPage';
import InPageQuickForm from '../quickForm/desktop/InPageQuickForm';
import InPagePdfForm from '../pdf/desktop/InPagePdfForm';
import withDocumentExport from '../helpers/withDocumentExport';
import Spinner from '../helpers/Spinner';

class RequestStepper extends React.Component {
  constructor(props) {
    super(props);
    this.componentId = Math.random();
    this.state = {
      startPageEnabled: props.embedMode && !props.skipStartPageOriginal,
      showingStartPage:
        props.embedMode && !props.skipStartPageOriginal && !props.scanEnded
    };
  }

  defineCurrentActionDocumentType = () =>
    this.isSmartFillModeActive()
      ? DOC_TYPE_FORM
      : this.props.actionDocument?.type;

  isSmartFillModeActive = () => {
    return (
      this.props.actionDocument &&
      this.props.actionDocument.smartFill &&
      !this.props.actionDocument.submitted &&
      !this.props.scanEnded &&
      this.state.returnedFromQuickForm !== this.props.actionDocument.id
    );
  };

  componentDidMount() {
    if (this.props.scanEnded) {
      this.props.goToRequestPage(this.props.requestPages.length - 1);
    } else {
      const notSubmittedYetPageIndex = this.props.requestPages.findIndex(
        p => !p.submitted
      );
      if (this.props.currentRequestPageIndex < notSubmittedYetPageIndex) {
        this.props.goToRequestPage(notSubmittedYetPageIndex);
      } else if (this.props.oneDocumentStep) {
        this.onNavigateToDocumentClick(this.props.requestedDocuments[0]);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.actionDocument?.id !== this.props.actionDocument?.id) {
      this.setState({
        returnedFromQuickForm: ''
      });
    }
    if (this.props.scanEnded && !prevProps.scanEnded) {
      gaeList('POST_MESSAGE', 'PWA_SEND_DOCUMENTS_DONE');
      window.parent.postMessage('PWA_SEND_DOCUMENTS_DONE', '*');
    }
    if (
      this.props.requesterSelfMode &&
      this.props.summaryStep &&
      !prevProps.summaryStep
    ) {
      gaeList('REQUESTER_SELF_SEND_DATA');
      this.props.onSendRequest();
    }
    if (this.props.activePageIndex !== prevProps.activePageIndex) {
      gaeList(
        'ACTIVE_PAGE_INDEX_CHANGED',
        `from ${prevProps.activePageIndex} to ${this.props.activePageIndex}`
      );

      if (this.props.oneDocumentStep) {
        //navigate direct to document
        this.onNavigateToDocumentClick(this.props.requestedDocuments[0]);
      } /*else {
        //remove active document from view, show step list
        this.props.goToRequestPage(this.props.activePageIndex);
      }*/
    }
    this.props.checkSignal(APP_EXTERNAL_EVENTS.APP_NEXT_ACTION, () =>
      this.doNextRequestAction()
    );
    this.props.checkSignal(APP_EXTERNAL_EVENTS.APP_PREV_ACTION, () =>
      this.doPrevRequestAction()
    );
    /*if (!prevProps.scanEnded && this.props.scanEnded) {
      this.onSendRequestDone();
    }*/
  }

  onPageIn = () => {
    gaListPageView();
  };

  loadDesktopImage(docId) {
    gaeList('START_FILE_UPLOAD');
    this.props.loadFromFile(docId, () => this.openDesktopGallery(docId));
  }

  openDesktopGallery = docId => {
    gaeList('DOCUMENT_PAGES_POPUP', 'SHOW', true);
    this.props.navigateToInPageDocument(
      this.props.getRequestedDocumentById(docId)
    );
  };

  onDocumentOpenClick = document => {
    switch (document.type) {
      case DOC_TYPE_DOCUMENT:
        gaClickOnDocType(document.type, document.id.substring(0, 4));
        this.openDesktopGallery(document.id);
        break;
      default:
        this.props.navigateToInPageDocument(document);
    }
  };

  onNavigateToDocumentClick = (document, pageIndex) => {
    if (pageIndex !== undefined) {
      this.props.goToRequestPage(pageIndex);
    }
    this.props.navigateToInPageDocument(document);
  };

  doNextRequestAction = forceNextStep => {
    if (this.state.showingStartPage) {
      this.setState({
        showingStartPage: false
      });
    } else if (this.props.summaryStep) {
      if (this.props.scanEnded) {
        window.parent.postMessage('PWA_NAVIGATE_BACK', '*');
      } else {
        this.props.onSendRequest();
      }
    } else if (!forceNextStep && this.props.actionDocument) {
      switch (this.defineCurrentActionDocumentType()) {
        case DOC_TYPE_FORM:
        case DOC_TYPE_ID_CHECK:
          if (
            this.isSmartFillModeActive() &&
            this.getCurrentActionDocumentInfo()?.currentPageIsLast &&
            (this.getCurrentActionDocumentInfo()?.currentPageIsValid ||
              this.props.actionDocument?.smartFill.allOptional)
          ) {
            this.setState({
              returnedFromQuickForm: this.props.actionDocument.id
            });

            return;
          }
          this.props.sendSignal(DOCUMENT_EXTERNAL_EVENTS.NEXT_STEP);
          break;
        case DOC_TYPE_PDF_FORM:
          this.props.sendSignal(DOCUMENT_EXTERNAL_EVENTS.NEXT_ELEMENT);
          break;
        case DOC_TYPE_IMMEDIATE_CONFIRMATION:
        case DOC_TYPE_INFORMATION:
          this.props.sendSignal(DOCUMENT_EXTERNAL_EVENTS.NEXT_STEP);
          break;
        default:
          this.props.goToNextRequestPage();
      }
    } else {
      this.props.goToNextRequestPage();
    }
  };

  hasActiveDocumentPrevAction = () => {
    if (!this.props.actionDocument) return false;

    switch (this.defineCurrentActionDocumentType()) {
      case DOC_TYPE_FORM:
      case DOC_TYPE_ID_CHECK: {
        const docStateInfo = this.getCurrentActionDocumentInfo();
        return docStateInfo && docStateInfo.currentPageIndex > 0;
      }
      default:
        return false;
    }
  };

  notifyParentNavigateBack = () => {
    gaeList('POST_MESSAGE', 'PWA_NAVIGATE_BACK');
    window.parent.postMessage('PWA_NAVIGATE_BACK', '*');
  };

  doPrevRequestAction = () => {
    if (this.state.showingStartPage) {
      this.notifyParentNavigateBack();
      return;
    }
    const navigateBackInStepper = () => {
      if (this.state.startPageEnabled && this.props.activePageIndex === 0) {
        this.setState({ showingStartPage: true });
      } else {
        this.props.goToPrevRequestPage();
      }
    };
    if (this.props.actionDocument) {
      switch (this.defineCurrentActionDocumentType()) {
        case DOC_TYPE_FORM:
        case DOC_TYPE_ID_CHECK:
          {
            const docStateInfo = this.getCurrentActionDocumentInfo();
            if (
              docStateInfo?.currentPageIndex === 0 ||
              (this.props.actionDocument.type === DOC_TYPE_ID_CHECK &&
                this.props.scanEnded)
            ) {
              navigateBackInStepper();
            } else {
              this.props.sendSignal(DOCUMENT_EXTERNAL_EVENTS.PREV_STEP);
            }
          }
          break;
        default:
          navigateBackInStepper();
      }
    } else {
      navigateBackInStepper();
    }
  };

  getCurrentActionDocumentInfo = () => {
    if (this.props.actionDocument) {
      return this.props.activeDocumentSharedState[
        this.defineCurrentActionDocumentType()
      ];
    }
  };

  onSendRequestDone = () => {
    f7navigator.forceBackToThankyou({
      displayMode: DOCUMENT_DISPLAY_MODE_DESKTOP_WINDOW
    });
  };

  goToRequestPage = index => {
    if (this.state.showingStartPage) {
      this.setState({ showingStartPage: false });
    }
    this.props.goToRequestPage(index);
  };

  render = () => {
    const {
      scanEnded,
      forms,
      documentsStatus,
      successSentKeys,
      errorSentKeys,
      currentActionForDocId,
      requestedDocuments,
      currentActionForDocType,
      currentActionForDocProps,
      activePageIndex,
      pagesInfo,
      activePageTitle,
      isManyPagesRequest,
      actionDocument,
      oneDocumentStep,
      actionDocumentIndex,
      templateMode,
      haveDocumentsToSend,
      summaryStep,
      removeCurrentActionDocument,
      goToPrevRequestPage,
      goToNextRequestPage,
      goToRequestPage,
      skipStartPage,
      embedMode,
      companyName,
      expired,
      caseName,
      companyLogo,
      requestMessage,
      completeButtonLabel,
      customConfirmationPageText,
      doDownloadDocument,
      doPrintDocument,
      requesterSelfMode,
      hideNavigation,
      onRejectDocument,
      documentRejectionAllowed
    } = this.props;

    const returnedFromQuickForm = this.state.returnedFromQuickForm;
    const smartFillFormModeMode = this.isSmartFillModeActive();

    const hideSideNavigation =
      requesterSelfMode &&
      ((requestedDocuments.length === 1 && pagesInfo?.length === 2) ||
        summaryStep);

    const hideFooterNavigation =
      hideNavigation || (requesterSelfMode && summaryStep);

    const activePageMainTitle = activePageTitle;
    let activePageSubTitle =
      actionDocument && !oneDocumentStep ? actionDocument.name : '';

    const actionDocumentInfo = this.getCurrentActionDocumentInfo();
    let customNextTitle = '';
    let customPrevBtnTitle = '';
    let currentPageIsValid = false;
    if (actionDocumentInfo) {
      currentPageIsValid = actionDocumentInfo.currentPageIsValid;
      customPrevBtnTitle = actionDocumentInfo.customPrevBtnTitle;

      switch (actionDocument.type) {
        case DOC_TYPE_IMMEDIATE_CONFIRMATION:
          customNextTitle = !actionDocument.done
            ? II18.get('title_confirm')
            : '';
          break;
        case DOC_TYPE_FORM:
          customNextTitle =
            currentPageIsValid && actionDocumentInfo.customNextBtnTitle;
          break;
        // no default
      }
    }
    if (embedMode && summaryStep && scanEnded) {
      customNextTitle = II18.get('title_close');
    }
    if (actionDocumentInfo && actionDocumentInfo.currentPageTitle) {
      activePageSubTitle +=
        (activePageSubTitle ? ' > ' : '') + actionDocumentInfo.currentPageTitle;
    }

    const headerTitle =
      this.props.caseName +
      (isManyPagesRequest && actionDocument && !oneDocumentStep
        ? ' > ' + actionDocument.name
        : '');

    const requestStepTitleAndInfo = (
      <div
        className={'requestStepTitleInfo'}
        style={{
          ...(this.state.showingStartPage && { display: 'none' })
        }}
      >
        <div className={'requestStepMainTitle'}>{activePageMainTitle}</div>
        {activePageSubTitle && (
          <div className={'requestStepSubTitle'}>{activePageSubTitle}</div>
        )}
      </div>
    );

    const pageContent = () => {
      return (
        <div id={'inpage-content-container'}>
          <div id={'requestStepContent'} className={'tinny-scrollbar'}>
            {this.state.showingStartPage ? (
              <StepperInPageContent
                contentTitle={activePageTitle}
                content={
                  <StartContent
                    companyName={companyName}
                    caseName={caseName}
                    companyLogo={companyLogo}
                    requestMessage={requestMessage}
                  />
                }
              />
            ) : summaryStep ? (
              requesterSelfMode ? (
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Spinner />
                </div>
              ) : (
                <StepperInPageContent
                  contentTitle={activePageTitle}
                  content={
                    <div
                      style={{
                        padding: '40px 0',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <div className={'request-stepper-result'}>
                        {scanEnded ? (
                          <div
                            style={{ borderRadius: 10, background: '#e1f1fd' }}
                          >
                            {expired ? (
                              <ExpiredWarningBlock />
                            ) : (
                              <SuccessMessageBlock
                                submittedDate={this.props.submittedDate}
                              />
                            )}
                          </div>
                        ) : customConfirmationPageText ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: customConfirmationPageText
                            }}
                          />
                        ) : (
                          <>
                            <p>{II18.get('request_stepper_check_and_send')}</p>
                            {pagesInfo
                              .filter(page => !page.summaryStep)
                              .map((page, index) => (
                                <div key={index}>
                                  <h4 onClick={() => goToRequestPage(index)}>
                                    {page.submitted ? (
                                      <Icon md="material:check" />
                                    ) : (
                                      `${index + 1}.`
                                    )}{' '}
                                    {page.title}
                                  </h4>
                                  {page.documents.length > 1 &&
                                    page.documents
                                      .filter(doc => doc.done)
                                      .map(doc => (
                                        <li
                                          onClick={() => {
                                            goToRequestPage(index);
                                            this.onNavigateToDocumentClick(doc);
                                          }}
                                          key={doc.id}
                                          className={'document'}
                                        >
                                          {doc.name}
                                        </li>
                                      ))}
                                </div>
                              ))}
                          </>
                        )}
                      </div>
                    </div>
                  }
                />
              )
            ) : actionDocument ? (
              actionDocument.type === DOC_TYPE_PAYMENT ? (
                <Payment
                  key={actionDocument.id}
                  requestedDocumentId={actionDocument.id}
                  scrollContainer={'requestStepContent'}
                  displayMode={DOCUMENT_DISPLAY_MODE_INPAGE}
                  onDocumentClose={goToNextRequestPage}
                />
              ) : actionDocument.type === DOC_TYPE_ID_CHECK ? (
                <IdCheckInPage
                  startFromFirstPage={this.props.requestStepMovedForward}
                  key={actionDocument.id}
                  requestedDocumentId={actionDocument.id}
                  scrollContainer={'requestStepContent'}
                  onDocumentClose={goToNextRequestPage}
                />
              ) : actionDocument.type === DOC_TYPE_INFORMATION ? (
                <Viewer
                  key={actionDocument.id}
                  requestedDocumentId={actionDocument.id}
                  displayMode={DOCUMENT_DISPLAY_MODE_INPAGE}
                  scrollContainer={'requestStepContent'}
                  onDocumentClose={goToNextRequestPage}
                />
              ) : actionDocument.type === DOC_TYPE_IMMEDIATE_CONFIRMATION ? (
                <Viewer
                  key={actionDocument.id}
                  requestedDocumentId={actionDocument.id}
                  displayMode={DOCUMENT_DISPLAY_MODE_INPAGE}
                  scrollContainer={'requestStepContent'}
                  onDocumentClose={goToNextRequestPage}
                />
              ) : actionDocument.type === DOC_TYPE_FORM ||
                smartFillFormModeMode ? (
                <InPageQuickForm
                  showTitleAndInfo={
                    hideNavigation &&
                    hideSideNavigation &&
                    requestStepTitleAndInfo
                  }
                  startFromLastPage={!this.props.requestStepMovedForward}
                  key={actionDocument.id}
                  requestedDocumentId={actionDocument.id}
                  onDocumentClose={goToNextRequestPage}
                  smartFillFormMode={smartFillFormModeMode}
                />
              ) : actionDocument.type === DOC_TYPE_PDF_FORM ? (
                <InPagePdfForm
                  showTitleAndInfo={
                    hideNavigation &&
                    hideSideNavigation &&
                    requestStepTitleAndInfo
                  }
                  key={actionDocument.id}
                  requestedDocumentId={actionDocument.id}
                  scrollContainer={'requestStepContent'}
                  onDocumentClose={goToNextRequestPage}
                  returnFromQuickForm={
                    returnedFromQuickForm === actionDocument.id
                  }
                  {...currentActionForDocProps}
                />
              ) : actionDocument.type === DOC_TYPE_DOCUMENT ? (
                <GalleryInPage
                  key={actionDocument.id}
                  showTitleAndInfo={
                    hideNavigation &&
                    hideSideNavigation &&
                    requestStepTitleAndInfo
                  }
                  requestedDocumentId={actionDocument.id}
                  onDocumentClose={goToNextRequestPage}
                />
              ) : (
                <div>Unsupported Document</div>
              )
            ) : (
              <StepperInPageContent
                contentIndex={activePageIndex + 1}
                contentTitle={activePageTitle}
                content={
                  <div
                    className={'desktop-card documents-list'}
                    style={{ padding: 10 }}
                  >
                    {requestedDocuments.map(document => (
                      <DesktopDocEntry
                        animateEntry={
                          this.props.highlightDocument === document.id
                        }
                        key={document.id}
                        document={document}
                        scannedPages={this.props.getScannedPages(document.id)}
                        forms={forms}
                        documentsStatus={documentsStatus}
                        onDesktopUploadClick={this.onDocumentOpenClick}
                        scanEnded={scanEnded}
                        successSentKeys={successSentKeys}
                        errorSentKeys={errorSentKeys}
                      />
                    ))}
                  </div>
                }
              />
            )}
          </div>

          {!hideFooterNavigation && (
            <div id={'inpage-footer'}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  {/*<span className={'stepNumber'}>{activePageIndex + 1}</span>*/}
                  {requestStepTitleAndInfo}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'end'
                  }}
                >
                  <Button
                    id={'btnPrevRequestAction'}
                    onClick={this.doPrevRequestAction}
                    style={{ marginRight: 10 }}
                    disabled={
                      !(
                        embedMode ||
                        activePageIndex > 0 ||
                        this.state.startPageEnabled ||
                        (actionDocument && actionDocumentIndex > 0) ||
                        (!oneDocumentStep && actionDocument) ||
                        this.hasActiveDocumentPrevAction()
                      )
                    }
                  >
                    {customPrevBtnTitle
                      ? customPrevBtnTitle
                      : II18.get('title_back')}
                  </Button>

                  <Button
                    disabled={scanEnded && summaryStep && !embedMode}
                    id={'btnNextRequestAction'}
                    onClick={() => this.doNextRequestAction()}
                    color={'blue'}
                    fill
                  >
                    {customNextTitle
                      ? customNextTitle
                      : summaryStep && !scanEnded && completeButtonLabel
                      ? completeButtonLabel
                      : II18.get(
                          summaryStep && !scanEnded
                            ? !haveDocumentsToSend || templateMode
                              ? 'list_done'
                              : sendTitleDefault(haveDocumentsToSend)
                            : 'title_next'
                        )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

    const actionsList = [];
    switch (actionDocument?.type) {
      case DOC_TYPE_PDF_FORM:
      case DOC_TYPE_INFORMATION:
      case DOC_TYPE_IMMEDIATE_CONFIRMATION:
        actionsList.push(
          {
            visible: true,
            id: 'download',
            onClick: () => doDownloadDocument(actionDocument.id),
            title: II18.get('title_download'),
            materialIcon: 'file_download'
          },
          {
            visible: true,
            id: 'print',
            onClick: () => doPrintDocument(actionDocument.id),
            title: II18.get('title_print'),
            materialIcon: 'print'
          }
        );
        break;
      // no default
    }
    if (
      documentRejectionAllowed &&
      !scanEnded &&
      actionDocument &&
      actionDocument.type !== DOC_TYPE_INFORMATION &&
      actionDocument.type !== DOC_TYPE_ID_CHECK &&
      actionDocument.type !== DOC_TYPE_PAYMENT &&
      !actionDocument.rejected
    ) {
      actionsList.push({
        visible: true,
        id: 'rejectDocument',
        onClick: () => onRejectDocument(actionDocument.id),
        title: II18.get('title_reject'),
        materialIcon: 'block',
        showTitle: actionsList.length === 0
      });
    }
    return (
      <Page
        onPageBeforeIn={this.onPageIn}
        onPageAfterIn={this.onPageAfterIn}
        id={'requestStepper'}
        className={`${requesterSelfMode ? 'requester-self' : ''} ${
          hideNavigation ? 'no-navigation' : ''
        }`}
      >
        {/*showThankyouPopUp && (
          <DesktopThankyouPopUp
            onClosePopUp={onCloseThankYouPopup}
            companyName={companyName}
          />
        )*/}
        <DesktopFullPage
          open={true}
          hideHeader={embedMode}
          disableBack={
            (skipStartPage || scanEnded) &&
            !(currentActionForDocType && requestedDocuments.length > 1)
          }
          goBack={
            currentActionForDocType && requestedDocuments.length > 1
              ? removeCurrentActionDocument
              : goToPrevRequestPage
          }
          popUpId={'docslist-popup'}
          documentName={headerTitle}
          sheetClassName={'docslist'}
          pageSideId={'docslist'}
          pageContent={pageContent()}
          hideButtonsSide={hideSideNavigation}
          additionalSideContent={
            <div>
              <RequestVerticalSteps
                hideSummaryStep={requesterSelfMode}
                pagesInfo={pagesInfo}
                activeStepIndex={activePageIndex}
                navigateToDocument={this.onNavigateToDocumentClick}
                goToRequestPage={this.goToRequestPage}
                actionDocId={currentActionForDocId}
              />
              {actionsList.length > 0 && (
                <>
                  <div className={'stepper-divider'} />
                  <div className={'stepperDocumentActions'}>
                    {actionsList.map(
                      action =>
                        action.visible && (
                          <div
                            id={action.id}
                            className={action.className}
                            key={action.id}
                            title={action.title}
                            onClick={action.onClick}
                          >
                            <Icon
                              material={action.materialIcon}
                              icon={action.icon}
                              slot={'before-title'}
                            />
                            {action.showTitle && action.title}
                          </div>
                        )
                    )}
                  </div>
                </>
              )}
            </div>
          }
        />
      </Page>
    );
  };
}

RequestStepper.propTypes = {
  documentRejectionAllowed: PropTypes.bool,
  onRejectDocument: PropTypes.func,
  requestMessage: PropTypes.string,
  completeButtonLabel: PropTypes.string,
  customConfirmationPageText: PropTypes.string,
  requesterSelfMode: PropTypes.bool,
  hideNavigation: PropTypes.bool,
  embedMode: PropTypes.bool,
  onSendRequest: PropTypes.func,
  skipStartPage: PropTypes.bool,
  isDocumentsReadyToSend: PropTypes.bool,
  haveDocumentsToSend: PropTypes.bool,
  highlightDocument: PropTypes.any,
  oneDocumentStep: PropTypes.bool,
  activeDocumentSharedState: PropTypes.object,
  getRequestedDocumentById: PropTypes.func,
  goToRequestPage: PropTypes.func,
  onlyInfoDocuments: PropTypes.bool,
  selectedLang: PropTypes.string,
  brandingDetails: PropTypes.object,
  cameraMode: PropTypes.bool,
  caseName: PropTypes.string,
  companyLogo: PropTypes.string,
  companyEmail: PropTypes.string,
  companyName: PropTypes.string,
  documentsStatus: PropTypes.object,
  errorMessage: PropTypes.string,
  expired: PropTypes.bool,
  forms: PropTypes.object,
  infoMessage: PropTypes.string,
  privacyAccepted: PropTypes.bool,
  requestedDocuments: PropTypes.array,
  scanEnded: PropTypes.bool,
  scannedDocuments: PropTypes.object,
  errorSentKeys: PropTypes.object,
  setCameraMode: PropTypes.func,
  setPrivacyAccepted: PropTypes.func,
  submittedDate: PropTypes.string,
  successSentKeys: PropTypes.object,
  templateMode: PropTypes.bool,
  requestPages: PropTypes.any,
  setCurrentRequestPageIndex: PropTypes.func,
  currentRequestPageIndex: PropTypes.number,
  actionDocument: PropTypes.object,
  requestStepMovedForward: PropTypes.bool,
  navigateToInPageDocument: PropTypes.func,
  summaryStep: PropTypes.bool,
  activePageDone: PropTypes.bool,
  returnFromQuickForm: PropTypes.bool,
  showThankyouPopUp: PropTypes.bool,
  componentId: PropTypes.string,

  doDownloadDocument: PropTypes.func,
  doPrintDocument: PropTypes.func,

  pagesInfo: PropTypes.array,
  activePageTitle: PropTypes.string,
  goToNextRequestPage: PropTypes.func,
  goToPrevRequestPage: PropTypes.func,
  activePageIndex: PropTypes.number,
  isManyPagesRequest: PropTypes.bool,
  getScannedPages: PropTypes.func,
  actionDocumentIndex: PropTypes.number,
  removeCurrentActionDocument: PropTypes.func,
  navigateBack: PropTypes.func,
  onCloseThankYouPopup: PropTypes.func,
  skipStartPageOriginal: PropTypes.bool,

  //withDesktopUpload
  loadFromFile: PropTypes.func,

  //withDocumentRootLogic
  currentActionForDocId: PropTypes.string,
  currentActionForDocType: PropTypes.string,
  currentActionForDocProps: PropTypes.object,
  sendSignal: PropTypes.func,
  checkSignal: PropTypes.func
};

export default withDocumentExport(withDesktopUpload(RequestStepper));
